import React, { useState, useEffect, useCallback } from "react";
import { getTags, getUserOrganizations, getDocument, getCurrentUser } from "../utils/Api";
import { useUpload } from "../contexts/UploadContext";
import { useAuth } from "../utils/AuthContext";
import useMobileDevice from "../hooks/useMobileDevice";

import { UploadCloud, AlertTriangle, FileIcon, X } from "lucide-react";

import PrimaryButton from "../components/Buttons/PrimaryButton";
import Modal from "./Modal";

type Mode = "create" | "add";
type InsertMode = "start" | "after" | "end";

interface DocumentUploadProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  mode?: Mode;
  documentID?: string | null;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  isOpen,
  setIsOpen,
  mode = "create",
  documentID: existingDocumentID = null,
}) => {
  const [images, setImages] = useState<File[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [documentName, setDocumentName] = useState<string>("");
  const [insertMode, setInsertMode] = useState<InsertMode>("end");
  const [insertPageNum, setInsertPageNum] = useState<number>(1);
  const [availableTags, setAvailableTags] = useState<
    Array<{ id: string; name: string; color: string }>
  >([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [organisations, setOrganisations] = useState<
    Array<{ id: string; name: string; allow_mobile_upload: boolean }>
  >([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState<string>("");
  const [existingDocumentName, setExistingDocumentName] = useState<string>("");
  const [pagesUploadedToday, setPagesUploadedToday] = useState<number>(0);
  const [dailyLimit, setDailyLimit] = useState<number>(Infinity);
  const [estimatedPageCount, setEstimatedPageCount] = useState<number>(0);
  const [isCountingPages, setIsCountingPages] = useState<boolean>(false);

  const { startUpload } = useUpload();
  const { accountType } = useAuth();
  const isMobile = useMobileDevice();

  const hasPageLimit = accountType === "free" || accountType === "individual";
  const remainingPages = Math.max(0, dailyLimit - pagesUploadedToday);
  const exceedsLimit = hasPageLimit && estimatedPageCount > remainingPages;

  const selectedOrg = organisations.find((o) => o.id === selectedOrganisation);
  const isMobileOrgRestricted = !!(
    isMobile &&
    selectedOrganisation !== "" &&
    selectedOrg &&
    !selectedOrg.allow_mobile_upload
  );

  const SUPPORTED_EXTENSIONS = [
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".webp",
    ".svg",
    ".bmp",
    ".tiff",
    ".tif",
    ".pdf",
  ];

  const SUPPORTED_MIME_TYPES = [
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
    "image/svg+xml",
    "image/bmp",
    "image/tiff",
    "application/pdf",
  ];

  const isFileSupported = (file: File): boolean => {
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    const mimeType = file.type.toLowerCase();

    return (
      SUPPORTED_EXTENSIONS.includes(extension) ||
      SUPPORTED_MIME_TYPES.includes(mimeType)
    );
  };

  const countPdfPages = useCallback(async (file: File): Promise<number> => {
    try {
      const buffer = await file.arrayBuffer();
      // Decode as latin-1 so binary bytes are preserved 1:1 as characters
      const text = new TextDecoder("latin-1").decode(buffer);

      // Primary: /Count N appears in every Page tree node; the root node's
      // value equals the total page count, which is always the largest.
      const countMatches = Array.from(text.matchAll(/\/Count\s+(\d+)/g));
      if (countMatches.length > 0) {
        const max = Math.max(...countMatches.map((m) => parseInt(m[1] ?? "0", 10)));
        if (max > 0) return max;
      }

      // Fallback for very old / non-compressed PDFs: count page-type objects
      const pageMatches = text.match(/\/Type\s*\/Page(?:[^s]|$)/g);
      if (pageMatches && pageMatches.length > 0) return pageMatches.length;

      return 1;
    } catch {
      return 1;
    }
  }, []);

  // Recount total pages whenever the file list changes
  useEffect(() => {
    if (!images || images.length === 0) {
      setEstimatedPageCount(0);
      return;
    }

    let cancelled = false;
    setIsCountingPages(true);

    const countAll = async () => {
      const counts = await Promise.all(
        images.map((file) => {
          const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
          const isPdf = ext === ".pdf" || file.type === "application/pdf";
          return isPdf ? countPdfPages(file) : Promise.resolve(1);
        }),
      );
      if (!cancelled) {
        setEstimatedPageCount(counts.reduce((a, b) => a + b, 0));
        setIsCountingPages(false);
      }
    };

    countAll();
    return () => { cancelled = true; };
  }, [images, countPdfPages]);

  useEffect(() => {
    if (isOpen) {
      let cancelled = false;
      setImages(null);
      setError(null);
      setDocumentName("");
      setInsertMode("end");
      setInsertPageNum(1);
      setSelectedTags([]);
      setSelectedOrganisation("");
      setExistingDocumentName("");
      setPagesUploadedToday(0);
      setDailyLimit(Infinity);
      setEstimatedPageCount(0);

      const loadUsage = async () => {
        try {
          const response = await getCurrentUser();
          if (!cancelled) {
            setPagesUploadedToday(response.data.pages_uploaded_today ?? 0);
            setDailyLimit(response.data.daily_limit ?? Infinity);
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to load usage info:", error);
          }
        }
      };

      const loadTags = async () => {
        try {
          const response = await getTags();
          if (!cancelled) {
            setAvailableTags(response.data.tags || []);
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to load tags:", error);
          }
        }
      };
      loadUsage();
      loadTags();

      const loadOrganisations = async () => {
        try {
          const response = await getUserOrganizations();
          if (!cancelled) {
            setOrganisations(response.data.organizations || []);
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to load organisations:", error);
          }
        }
      };
      loadOrganisations();

      if (mode === "add" && existingDocumentID) {
        const loadDocumentName = async () => {
          try {
            const response = await getDocument(existingDocumentID);
            if (!cancelled) {
              setExistingDocumentName(response.data.name || "Document");
            }
          } catch (error) {
            if (!cancelled) {
              console.error("Failed to load document name:", error);
              setExistingDocumentName("Document");
            }
          }
        };
        loadDocumentName();
      }

      return () => {
        cancelled = true;
      };
    }
  }, [isOpen, mode, existingDocumentID]);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const droppedFiles = Array.from(event.dataTransfer.files) as File[];
    if (droppedFiles.length === 0) return;

    const supportedFiles = droppedFiles.filter(isFileSupported);
    const unsupportedFiles = droppedFiles.filter(
      (file) => !isFileSupported(file),
    );

    if (unsupportedFiles.length > 0) {
      const unsupportedNames = unsupportedFiles.map((f) => f.name).join(", ");
      setError(
        `Unsupported file type(s): ${unsupportedNames}. This file type is not supported. Please upload one of the allowed formats instead.`,
      );
    } else {
      setError(null);
    }

    if (supportedFiles.length > 0) {
      setImages((prevImages) =>
        prevImages ? [...prevImages, ...supportedFiles] : supportedFiles,
      );
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const newFiles = Array.from(event.target.files) as File[];

      const supportedFiles = newFiles.filter(isFileSupported);
      const unsupportedFiles = newFiles.filter(
        (file) => !isFileSupported(file),
      );

      if (unsupportedFiles.length > 0) {
        const unsupportedNames = unsupportedFiles.map((f) => f.name).join(", ");
        setError(
          `Unsupported file type(s): ${unsupportedNames}. This file type is not supported. Please upload one of the allowed formats instead.`,
        );
      } else {
        setError(null);
      }

      if (supportedFiles.length > 0) {
        setImages((prevImages) =>
          prevImages ? [...prevImages, ...supportedFiles] : supportedFiles,
        );
      }
    }
  };

  const handleRemoveFile = (indexToRemove: number) => {
    if (!images) return;

    const updatedFiles = images.filter((_, index) => index !== indexToRemove);

    if (updatedFiles.length === 0) {
      setImages(null);
    } else {
      setImages(updatedFiles);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!images) return;

    if (mode === "create" && !documentName.trim()) {
      setError("Document name is required.");
      return;
    }

    if (mode === "add" && !existingDocumentID) {
      setError("Document ID is required for adding pages.");
      return;
    }

    startUpload({
      documentName:
        mode === "create"
          ? documentName
          : `Adding pages to ${existingDocumentName}`,
      files: images,
      mode,
      selectedTags,
      selectedOrganisation,
      ...(existingDocumentID ? { existingDocumentId: existingDocumentID } : {}),
      ...(mode === "add" ? { insertMode, insertPageNum } : {}),
    });

    setIsOpen(false);
  };

  const modalTitle =
    mode === "create" ? "Create new document" : "Add pages to document";

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <h1 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
        {modalTitle}
      </h1>
      <p className="text-sm text-gray-500 mb-5">
        Upload your files to get started.
      </p>

      {isMobileOrgRestricted && (
        <div className="mb-6 bg-amber-50 text-amber-700 text-sm p-4 rounded-lg border border-amber-200 flex items-start gap-3">
          <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
          <span>
            Uploads are disabled on mobile for Organisation accounts to ensure
            IP security. Please use the Desktop application for all document
            submissions.
          </span>
        </div>
      )}

      {hasPageLimit && remainingPages === 0 && (
        <div className="mb-6 bg-red-50 text-red-600 text-sm p-4 rounded-lg border border-red-200 flex items-start gap-3">
          <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
          <span>
            You have reached your daily page limit ({dailyLimit} pages). Your limit resets
            at midnight UTC.{" "}
            <a href="/pricingPlans" className="underline font-medium hover:text-red-800">
              Upgrade your plan
            </a>{" "}
            for more pages.
          </span>
        </div>
      )}

      {exceedsLimit && remainingPages > 0 && (
        <div className="mb-6 bg-amber-50 text-amber-700 text-sm p-4 rounded-lg border border-amber-200 flex items-start gap-3">
          <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
          <span>
            Your selected files contain an estimated{" "}
            <span className="font-semibold">{estimatedPageCount} page{estimatedPageCount !== 1 ? "s" : ""}</span>, but
            only{" "}
            <span className="font-semibold">{remainingPages}</span>{" "}
            page{remainingPages !== 1 ? "s" : ""} remain in your daily limit. Remove
            some files or{" "}
            <a href="/pricingPlans" className="underline font-medium hover:text-amber-900">
              upgrade your plan
            </a>{" "}
            for more pages.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div
            className="bg-gray-50/50 border border-dashed border-gray-200 rounded-lg p-6 text-center"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <UploadCloud className="text-[#1d365d] mx-auto mb-3" size={36} />

            <label
              htmlFor="file-upload"
              className="text-sm font-medium text-[#1d365d] cursor-pointer hover:text-[#2d4a75] transition-colors"
            >
              Choose files
            </label>

            <span className="text-sm text-gray-400"> or drag and drop</span>

            <input
              id="file-upload"
              type="file"
              multiple
              className="sr-only"
              onChange={handleFileChange}
              accept="image/*,application/pdf"
            />

            <p className="text-xs text-gray-500 mt-3">
              PNG, JPG, JPEG, GIF, WEBP, SVG, BMP, TIFF, PDF
            </p>

            {images && (
              <div className="mt-4 text-left">
                <h3 className="font-semibold text-sm text-gray-900 mb-2">
                  {images.length} file(s) selected
                  {isCountingPages ? (
                    <span className="ml-2 text-gray-400 font-normal">(counting pages…)</span>
                  ) : hasPageLimit ? (
                    <span className="ml-2 text-gray-500 font-normal">— {estimatedPageCount} page{estimatedPageCount !== 1 ? "s" : ""} estimated</span>
                  ) : null}:
                </h3>

                <ul className="max-h-24 overflow-y-auto space-y-1 pr-2 no-scrollbar">
                  {images.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 text-xs text-gray-700 group"
                    >
                      <FileIcon size={14} />
                      <span className="truncate flex-1">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="p-1 rounded text-red-600 hover:bg-red-100 hover:text-red-700 transition-colors"
                        title="Remove file"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              {mode === "create" && (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="doc-name"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      Document name
                    </label>
                    <input
                      id="doc-name"
                      type="text"
                      placeholder="E.g., Lab notes"
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                      required
                      className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                    />
                  </div>

                  {organisations.length > 0 && (
                    <div>
                      <label
                        htmlFor="organisation-select"
                        className="block text-sm font-medium text-gray-700 mb-1.5"
                      >
                        Organisation (optional)
                      </label>
                      <select
                        id="organisation-select"
                        value={selectedOrganisation}
                        onChange={(e) =>
                          setSelectedOrganisation(e.target.value)
                        }
                        className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                      >
                        <option value="">Personal (no organisation)</option>
                        {organisations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1.5 text-xs text-gray-400">
                        Choose an organisation to share this document with its
                        members.
                      </p>
                    </div>
                  )}

                  {availableTags.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Tags (optional)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map((tag) => (
                          <label
                            key={tag.id}
                            className="inline-flex items-center cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTags.includes(tag.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTags([...selectedTags, tag.id]);
                                } else {
                                  setSelectedTags(
                                    selectedTags.filter((id) => id !== tag.id),
                                  );
                                }
                              }}
                              className="sr-only"
                            />
                            <span
                              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                                selectedTags.includes(tag.id)
                                  ? "ring-2 ring-offset-1"
                                  : "opacity-60 hover:opacity-100"
                              }`}
                              style={{
                                backgroundColor: `${tag.color}20`,
                                color: tag.color,
                                border: `1px solid ${tag.color}40`,
                              }}
                            >
                              {tag.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {mode === "add" && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">
                    Insertion Point
                  </p>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="insert-mode"
                        value="start"
                        checked={insertMode === "start"}
                        onChange={(e) =>
                          setInsertMode(e.target.value as InsertMode)
                        }
                        className="form-radio text-sky-600 bg-white border-gray-300"
                      />
                      <span className="text-sm text-gray-700">
                        At the beginning
                      </span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="insert-mode"
                        value="after"
                        checked={insertMode === "after"}
                        onChange={(e) =>
                          setInsertMode(e.target.value as InsertMode)
                        }
                        className="form-radio text-sky-600 bg-white border-gray-300"
                      />
                      <span className="text-sm text-gray-700">After page</span>
                      <input
                        type="number"
                        value={insertPageNum}
                        onChange={(e) =>
                          setInsertPageNum(parseInt(e.target.value, 10))
                        }
                        disabled={insertMode !== "after"}
                        min="1"
                        className="w-16 p-1 text-base border text-gray-900 bg-white border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500 focus:ring-sky-500 focus:border-sky-500"
                      />
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="insert-mode"
                        value="end"
                        checked={insertMode === "end"}
                        onChange={(e) =>
                          setInsertMode(e.target.value as InsertMode)
                        }
                        className="form-radio text-sky-600 bg-white border-gray-300"
                      />
                      <span className="text-sm text-gray-700">At the end</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-200">
                <span>{error}</span>
              </div>
            )}

            <PrimaryButton
              type="submit"
              disabled={!images || isMobileOrgRestricted || exceedsLimit || isCountingPages || (hasPageLimit && remainingPages === 0)}
              className="flex items-center justify-center gap-2 w-full"
            >
              <span>{mode === "create" ? "Upload" : "Add Pages"}</span>
            </PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default DocumentUpload;
